import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Newspaper, CheckSquare, FileText, Send, AlertCircle } from 'lucide-react';
import BackOfficeMenu from '../components/BackOfficeMenu';
import ValidationTab from './tabs/ValidationTab';
import ArticlesTab from './tabs/ArticlesTab';
import NewsletterTab from './tabs/NewsletterTab';
import { usePermissions } from '../../utils/usePermissions';
import './ActualitesNewsletter.css';

export default function ActualitesNewsletter({ onNavigate, onLogout, currentPage = 'actualites-newsletter' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission, typeUser } = usePermissions();

  const isAdmin = typeUser === 'admin';
  const canAccessNews = isAdmin || hasPermission('actualites', 'read');
  const canAccessNewsletter = isAdmin || hasPermission('newsletter', 'read');

  // Determine initial tab from URL ?tab=... or default according to permissions
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'validation' && canAccessNews) return 'validation';
    if (tabParam === 'articles' && canAccessNews) return 'articles';
    if (tabParam === 'newsletter' && canAccessNewsletter) return 'newsletter';

    // Defaults based on permissions
    if (canAccessNews) return 'articles';
    if (canAccessNewsletter) return 'newsletter';
    return 'articles';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  // Sync tab with URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && ['validation', 'articles', 'newsletter'].includes(tabParam)) {
      if (tabParam === 'newsletter' && !canAccessNewsletter) {
        // Not permitted
        return;
      }
      if ((tabParam === 'validation' || tabParam === 'articles') && !canAccessNews) {
        // Not permitted
        return;
      }
      setActiveTab(tabParam);
    }
  }, [location.search, canAccessNews, canAccessNewsletter]);

  const switchTab = (tabKey) => {
    setActiveTab(tabKey);
    navigate(`/actualites-newsletter?tab=${tabKey}`, { replace: true });
  };

  const renderContent = () => {
    if (activeTab === 'validation') {
      if (!canAccessNews) {
        return (
          <div className="an-access-denied-box">
            <AlertCircle size={32} className="text-danger mb-2" />
            <h3>Accès restreint</h3>
            <p>Vous n'avez pas la permission de consulter la validation des actualités (actualites:read).</p>
          </div>
        );
      }
      return <ValidationTab />;
    }

    if (activeTab === 'articles') {
      if (!canAccessNews) {
        return (
          <div className="an-access-denied-box">
            <AlertCircle size={32} className="text-danger mb-2" />
            <h3>Accès restreint</h3>
            <p>Vous n'avez pas la permission de gérer les articles (actualites:read).</p>
          </div>
        );
      }
      return <ArticlesTab />;
    }

    if (activeTab === 'newsletter') {
      if (!canAccessNewsletter) {
        return (
          <div className="an-access-denied-box">
            <AlertCircle size={32} className="text-danger mb-2" />
            <h3>Accès restreint</h3>
            <p>Vous n'avez pas la permission d'accéder au module newsletter et abonnés (newsletter:read).</p>
          </div>
        );
      }
      return <NewsletterTab />;
    }

    return null;
  };

  return (
    <div className="rh-dashboard-container an-dashboard-root">
      {/* Sidebar BackOffice Menu */}
      <BackOfficeMenu
        onNavigate={onNavigate}
        onLogout={onLogout}
        currentPage={currentPage}
      />

      <div className="rh-dashboard-content an-main-content">
        {/* Hero Section */}
        <section className="rh-hero an-hero">
          <div className="rh-hero-info">
            <div className="rh-hero-icon an-hero-icon-gradient">
              <Newspaper color="white" size={36} />
            </div>
            <div className="rh-hero-text">
              <h1>Actualités & Newsletter</h1>
              <p>
                Gérez la veille réglementaire tunisienne, la publication des articles et vos campagnes de diffusion
              </p>
            </div>
          </div>
        </section>

        {/* Top Synchronized Navigation Tabs */}
        <div className="an-main-tabs">
          {canAccessNews && (
            <button
              type="button"
              className={`an-main-tab-btn ${activeTab === 'validation' ? 'active' : ''}`}
              onClick={() => switchTab('validation')}
            >
              <CheckSquare size={18} />
              <span>1. Validation des Actualités</span>
            </button>
          )}

          {canAccessNews && (
            <button
              type="button"
              className={`an-main-tab-btn ${activeTab === 'articles' ? 'active' : ''}`}
              onClick={() => switchTab('articles')}
            >
              <FileText size={18} />
              <span>2. Gestion des Articles</span>
            </button>
          )}

          {canAccessNewsletter && (
            <button
              type="button"
              className={`an-main-tab-btn ${activeTab === 'newsletter' ? 'active' : ''}`}
              onClick={() => switchTab('newsletter')}
            >
              <Send size={18} />
              <span>3. Newsletter & Abonnés</span>
            </button>
          )}
        </div>

        {/* Active Tab Content Area */}
        <div className="an-tab-content-wrapper">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

